import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import API_URL from '../config/api';
import AuthShell from '../components/AuthShell';
import OrderCard, { OrderDetail } from '../components/OrderCard';

/**
 * Public page opened from the "Confirm / Decline" links in the new-order
 * email. Authenticated by the order's secret token, so no login is needed.
 */
const OrderRespond: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [message, setMessage] = useState<{ text: string; tone: 'ok' | 'warn' | 'err' } | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/orders/respond/${token}/`);
      if (r.status === 404) {
        setNotFound(true);
        return;
      }
      setOrder(await r.json());
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const respond = async (_o: OrderDetail, action: 'confirm' | 'decline', reason?: string) => {
    const r = await fetch(`${API_URL}/api/orders/respond/${token}/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, reason: reason || '' }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) {
      if (data.order) setOrder(data.order);
      setMessage({ text: data.error || t('common.genericError'), tone: 'err' });
      return;
    }
    setOrder(data);
    setMessage({ text: action === 'confirm' ? t('orders.confirmedMsg') : t('orders.declinedMsg'), tone: 'ok' });
  };

  // Pre-select the action from the email link (?action=decline opens the reason box)
  const intent = params.get('action');

  let statusNote: string | null = null;
  if (order && !message) {
    if (order.confirmation_status === 'confirmed') statusNote = t('orders.confirmedMsg');
    else if (order.confirmation_status === 'declined') statusNote = t('orders.declinedMsg');
    else if (order.confirmation_status === 'expired') statusNote = t('orders.expiredMsg');
    else if (order.status !== 'open') statusNote = t('orders.closedMsg');
  }

  return (
    <AuthShell title={t('orders.respondTitle')} subtitle={order?.confirmation_status === 'pending' ? t('orders.respondBody') : undefined} wide>
      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : notFound || !order ? (
        <div className="bg-white shadow rounded-lg p-6 text-center text-sm text-gray-700">{t('orders.notFound')}</div>
      ) : (
        <div className="space-y-4">
          {(message || statusNote) && (
            <div
              className={`rounded-md p-4 text-sm ${
                message?.tone === 'err' ? 'bg-red-50 text-red-800' : message?.tone === 'ok' || order.confirmation_status === 'confirmed' ? 'bg-green-50 text-green-800' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {message?.text || statusNote}
            </div>
          )}
          <OrderCard order={order} onRespond={respond} compact />
          {intent === 'decline' && order.confirmation_status === 'pending' && !message && (
            <p className="text-xs text-center text-gray-500">{t('orders.respondBody')}</p>
          )}
          <p className="text-center text-sm text-gray-600">
            <Link to="/orders" className="text-blue-600">{t('orders.signInHint')}</Link>
          </p>
        </div>
      )}
    </AuthShell>
  );
};

export default OrderRespond;
